import React from "react";
import CommonModal from "./CommonModal";
import Button from "./Button";
import { AlertCircle, CheckCircle2 } from "lucide-react";

interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  type?: "danger" | "info" | "success" | "warning";
  size?: "sm" | "md" | "lg" | "xl";
  children?: React.ReactNode;
  confirmDisabled?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onOpenChange,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  loading = false,
  type = "info",
  size = "sm",
  children,
  confirmDisabled = false,
}) => {
  const typeConfigs = {
    danger: {
      icon: <AlertCircle className="w-12 h-12 text-destructive" />,
      buttonVariant: "danger" as const,
      iconBg: "bg-destructive/10",
    },
    info: {
      icon: <AlertCircle className="w-12 h-12 text-primary" />,
      buttonVariant: "primary" as const,
      iconBg: "bg-primary/10",
    },
    success: {
      icon: <CheckCircle2 className="w-12 h-12 text-green-500" />,
      buttonVariant: "primary" as const,
      iconBg: "bg-green-100",
    },
    warning: {
      icon: <AlertCircle className="w-12 h-12 text-amber-500" />,
      buttonVariant: "primary" as const,
      iconBg: "bg-amber-100",
    },
  };

  // Fallback to "info" if an unknown type is passed (prevents crashes)
  const config = typeConfigs[type] ?? typeConfigs["info"];

  return (
    <CommonModal open={open} onOpenChange={onOpenChange} size={size}>
      <div className="flex flex-col items-center text-center p-2">
        <div className={`p-3 rounded-full mb-4 ${config.iconBg}`}>
          {config.icon}
        </div>

        <h3 className="text-xl font-bold text-foreground mb-2">
          {title}
        </h3>

        <p className={`text-muted-foreground text-sm leading-relaxed ${children ? 'mb-4' : 'mb-8'}`}>
          {message}
        </p>

        {children && <div className="w-full mb-6 text-left">{children}</div>}

        <div className="flex items-center gap-3 w-full">
          <Button
            variant="ghost"
            className="flex-1 font-semibold"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            {cancelText}
          </Button>
          <Button
            className="flex-1 font-semibold"
            onClick={onConfirm}
            isLoading={loading}
            disabled={loading || confirmDisabled}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default ConfirmationModal;
